import React from 'react';
import { Link, useNavigate } from 'react-router-dom';

const Features: React.FC = () => {
    const navigate = useNavigate();

    return (
        <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
            {/* Navigation Header */}
            <nav className="bg-emerald-900 text-white sticky top-0 z-50 shadow-lg">
                <div className="container mx-auto max-w-6xl px-6 flex justify-between items-center h-16">
                    <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-emerald-700 shadow-inner">
                            <span className="text-xl">🏠</span>
                        </div>
                        <span className="font-bold text-lg tracking-tight">Landlord Pro</span>
                    </Link>

                    <div className="flex items-center gap-6">
                        <Link
                            to="/"
                            className="text-emerald-200 hover:text-white transition-colors text-sm font-medium"
                        >
                            Home
                        </Link>
                        <Link
                            to="/features"
                            className="text-white font-semibold text-sm border-b-2 border-emerald-400 pb-1"
                        >
                            Features
                        </Link>
                        <Link
                            to="/login"
                            className="text-emerald-200 hover:text-white transition-colors text-sm font-medium"
                        >
                            Login
                        </Link>
                        <button
                            onClick={() => navigate('/signup')}
                            className="bg-emerald-500 hover:bg-emerald-400 text-white px-5 py-2 rounded-lg font-semibold transition-all shadow-md hover:shadow-lg text-sm"
                        >
                            Start Free
                        </button>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <header className="bg-gradient-to-br from-emerald-900 via-emerald-800 to-slate-900 text-white pt-16 pb-20 px-6 relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySDI0di0yaC0xMnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-50"></div>
                <div className="container mx-auto max-w-5xl relative z-10 text-center">
                    <div className="inline-block bg-emerald-600/30 backdrop-blur-sm border border-emerald-400/30 rounded-full px-4 py-2 mb-6">
                        <span className="text-emerald-300 text-sm font-medium">🛡️ The Legal Shield</span>
                    </div>
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6 leading-tight">
                        If this ends up in court,<br />
                        <span className="text-emerald-400">can you prove you did everything right?</span>
                    </h1>
                    <p className="text-lg md:text-xl text-emerald-100 max-w-4xl mx-auto mb-10 leading-relaxed">
                        Landlord Pro is the <strong className="text-white">legal shield</strong> for UK letting agents, landlords, housing associations, and corporate portfolios — built on AWS-elastic infrastructure that scales from <strong className="text-white">20 to 50,000 properties</strong> on the same code.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <a
                            href="https://calendar.app.google/E2NdqWZSh2csxTAC7"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-8 py-4 bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold rounded-lg transition-all shadow-lg text-lg"
                        >
                            Book a 15-minute platform demo →
                        </a>
                        <Link to="/" className="px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/30 text-white hover:bg-white/20 font-bold rounded-lg transition-all text-lg">
                            View Pricing
                        </Link>
                    </div>
                </div>
            </header>

            {/* Comparison Table - Quick Visual */}
            <section className="py-16 px-6 bg-white border-b border-slate-200">
                <div className="container mx-auto max-w-5xl">
                    <div className="text-center mb-12">
                        <h2 className="text-2xl md:text-3xl font-bold mb-4">Why Landlord Pro?</h2>
                        <p className="text-slate-600">See how we compare to traditional property management systems.</p>
                    </div>

                    <div className="overflow-x-auto rounded-xl shadow-lg border border-slate-200">
                        <table className="w-full text-left border-collapse bg-white">
                            <thead>
                                <tr className="bg-slate-900 text-white">
                                    <th className="py-4 px-6 font-semibold"></th>
                                    <th className="py-4 px-6 font-bold text-emerald-400 text-lg">Landlord Pro</th>
                                    <th className="py-4 px-6 font-semibold text-slate-400">Traditional PMS</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                <tr className="hover:bg-emerald-50/50 transition-colors">
                                    <td className="py-4 px-6 font-medium text-slate-700">Document Scanning</td>
                                    <td className="py-4 px-6">
                                        <span className="text-emerald-700 font-semibold">Legal Logic Audit</span>
                                        <span className="text-slate-500 text-sm block">Checks semicolons/addresses</span>
                                    </td>
                                    <td className="py-4 px-6 text-slate-500">Basic Cloud Storage only</td>
                                </tr>
                                <tr className="hover:bg-emerald-50/50 transition-colors">
                                    <td className="py-4 px-6 font-medium text-slate-700">Regulatory Alerts</td>
                                    <td className="py-4 px-6">
                                        <span className="text-emerald-700 font-semibold">Enforcement Readiness</span>
                                        <span className="text-slate-500 text-sm block">Built for Dec 27th powers</span>
                                    </td>
                                    <td className="py-4 px-6 text-slate-500">Simple Expiry Reminders</td>
                                </tr>
                                <tr className="hover:bg-emerald-50/50 transition-colors">
                                    <td className="py-4 px-6 font-medium text-slate-700">Financial Tracking</td>
                                    <td className="py-4 px-6">
                                        <span className="text-emerald-700 font-semibold">Institutional Transparency</span>
                                        <span className="text-slate-500 text-sm block">IFRS 16/MTD Ready</span>
                                    </td>
                                    <td className="py-4 px-6 text-slate-500">Basic Bookkeeping</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* Who We Serve — 4 audience segments */}
            <section className="py-20 px-6 bg-gradient-to-b from-slate-50 to-white border-b border-slate-200">
                <div className="container mx-auto max-w-screen-2xl">
                    <div className="text-center mb-12">
                        <div className="inline-block bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                            🎯 Who We Serve
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">One platform. Every portfolio size.</h2>
                        <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                            Whether you manage one rental or fifty thousand, the legal-shield logic is the same — only the scale changes.
                        </p>
                    </div>

                    <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">

                        {/* New Landlord */}
                        <div className="bg-white border-2 border-emerald-200 rounded-2xl p-6 flex flex-col hover:shadow-xl hover:border-emerald-400 transition-all">
                            <div className="text-4xl mb-3" aria-hidden="true">🆕</div>
                            <h3 className="text-lg font-bold mb-3 leading-snug text-slate-900">New Landlord</h3>
                            <p className="text-sm text-slate-600 leading-relaxed flex-grow mb-4">
                                1 Property — <span className="font-bold text-emerald-700">FREE</span> forever. Get protected from day one with the Evidence Vault and PRS readiness scoring.
                            </p>
                            <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 rounded-full px-3 py-1 self-start">£0/mo</span>
                        </div>

                        {/* Independent Landlords, Estate & Letting Agents */}
                        <div className="bg-white border-2 border-slate-200 rounded-2xl p-6 flex flex-col hover:shadow-xl hover:border-emerald-400 transition-all">
                            <div className="text-4xl mb-3" aria-hidden="true">🏠</div>
                            <h3 className="text-lg font-bold mb-3 leading-snug text-slate-900">
                                Independent Landlords, Estate &amp; Letting Agents
                            </h3>
                            <p className="text-sm text-slate-600 leading-relaxed flex-grow mb-4">
                                2 to 100 properties. Court-grade Evidence Vault, AI Legal Q&amp;A, Renters' Rights Act 2025 notice generation, irrefutable audit trails.
                            </p>
                            <span className="text-xs font-semibold text-slate-700 bg-slate-100 rounded-full px-3 py-1 self-start">£14.99 – £49.99/mo</span>
                        </div>

                        {/* Housing Associations / Registered Providers / Social Landlords */}
                        <div className="bg-white border-2 border-slate-200 rounded-2xl p-6 flex flex-col hover:shadow-xl hover:border-emerald-400 transition-all">
                            <div className="text-4xl mb-3" aria-hidden="true">🏘️</div>
                            <h3 className="text-lg font-bold mb-3 leading-snug text-slate-900">
                                Housing Associations, Registered Providers or Social Landlords
                            </h3>
                            <p className="text-sm text-slate-600 leading-relaxed flex-grow mb-4">
                                101 – 5,000 properties. Awaab's Law ready, Tenant Satisfaction Measures (TSM) evidence packs, AI Legal Q&amp;A trained on social housing law.
                            </p>
                            <span className="text-xs font-semibold text-slate-700 bg-slate-100 rounded-full px-3 py-1 self-start">Bespoke pricing</span>
                        </div>

                        {/* Corporate Portfolio Operators / REITs */}
                        <div className="bg-white border-2 border-slate-200 rounded-2xl p-6 flex flex-col hover:shadow-xl hover:border-emerald-400 transition-all">
                            <div className="text-4xl mb-3" aria-hidden="true">🏢</div>
                            <h3 className="text-lg font-bold mb-3 leading-snug text-slate-900">
                                Corporate Portfolio Operators, REITs, Portfolio Owners
                            </h3>
                            <p className="text-sm text-slate-600 leading-relaxed flex-grow mb-4">
                                5,001+ units. Versioned API, white-label branding, OWASP-hardened security, elastic AWS-serverless infrastructure.
                            </p>
                            <span className="text-xs font-semibold text-slate-700 bg-slate-100 rounded-full px-3 py-1 self-start">Enterprise contract</span>
                        </div>

                    </div>

                    {/* CTA below cards */}
                    <div className="text-center mt-12">
                        <a
                            href="https://calendar.app.google/E2NdqWZSh2csxTAC7"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all shadow-lg"
                        >
                            Book a 15-minute platform demo →
                        </a>
                    </div>
                </div>
            </section>

            {/* Feature 1: Litigation-Ready Document Auditor */}
            <section className="py-20 px-6 bg-slate-50">
                <div className="container mx-auto max-w-6xl">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h2 className="text-3xl md:text-4xl font-bold mb-6">
                                The Litigation-Ready Document Auditor
                            </h2>
                            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                                Traditional systems store files; Landlord Pro <strong className="text-slate-900">audits the logic</strong> of those files to prevent catastrophic legal failure.
                            </p>

                            <div className="space-y-6">
                                <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                                    <div className="flex gap-4">
                                        <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center shrink-0 shadow-lg shadow-red-200">
                                            <span className="text-white text-xl">;</span>
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold mb-2">The Semicolon Guard</h3>
                                            <p className="text-slate-600 text-sm">Our engine performs a <strong>syntax scan</strong> of tenancy agreements. As seen in recent cases, a single missing semicolon in a service clause can lead to a failed eviction and lost revenue.</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                                    <div className="flex gap-4">
                                        <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg flex items-center justify-center shrink-0 shadow-lg shadow-amber-200">
                                            <span className="text-white text-xl">📍</span>
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold mb-2">CP12 Address Validation</h3>
                                            <p className="text-slate-600 text-sm">We automatically cross-reference landlord addresses on Gas Safety Certificates. A missing or incorrect address on a CP12 can render it <strong>non-compliant</strong> and invalidate a Section 21 notice.</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                                    <div className="flex gap-4">
                                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shrink-0 shadow-lg shadow-blue-200">
                                            <span className="text-white text-xl">🔐</span>
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold mb-2">Digital Integrity</h3>
                                            <p className="text-slate-600 text-sm">We ensure your "Evidence Vault" holds records that meet the highest technical standards of professional bodies like <strong>RICS</strong>, effective from January 2025.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-8 text-white relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/20 rounded-full blur-3xl"></div>
                            <div className="relative z-10">
                                <div className="text-6xl mb-6">⚠️</div>
                                <h3 className="text-2xl font-bold mb-4">Did You Know?</h3>
                                <p className="text-slate-300 mb-6">A single missing semicolon in a tenancy agreement recently led to a failed eviction, costing the landlord <strong className="text-white">£15,000+</strong> in legal fees and lost rent.</p>
                                <div className="border-t border-slate-700 pt-6">
                                    <p className="text-emerald-400 font-semibold">Landlord Pro's NLP engine catches these issues automatically.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Feature 2: Investigatory Response Hub */}
            <section className="py-20 px-6 bg-white">
                <div className="container mx-auto max-w-6xl">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div className="order-2 lg:order-1 bg-gradient-to-br from-emerald-600 to-emerald-800 rounded-2xl p-8 text-white relative overflow-hidden">
                            <div className="absolute bottom-0 left-0 w-48 h-48 bg-emerald-400/20 rounded-full blur-3xl"></div>
                            <div className="relative z-10">
                                <div className="text-6xl mb-6">🏛️</div>
                                <h3 className="text-2xl font-bold mb-4">From 27 December 2025</h3>
                                <p className="text-emerald-100 mb-6">Local housing authorities gain <strong className="text-white">enhanced investigatory powers</strong> to demand digital proof of compliance and enter premises.</p>
                                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                                    <p className="text-sm text-emerald-100">Generate a verified "Compliance Pack" in <strong className="text-white">seconds</strong>, not hours.</p>
                                </div>
                            </div>
                        </div>

                        <div className="order-1 lg:order-2">
                            <h2 className="text-3xl md:text-4xl font-bold mb-6">
                                The Investigatory Response Hub
                            </h2>
                            <p className="text-lg text-slate-600 mb-8">
                                <strong className="text-emerald-700">Audit-Ready in Seconds.</strong> Neutralise council friction by generating verified compliance packs instantly.
                            </p>

                            <div className="space-y-6">
                                <div className="flex gap-4 items-start">
                                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center shrink-0">
                                        <span className="text-emerald-600 text-lg">⚡</span>
                                    </div>
                                    <div>
                                        <h3 className="font-bold mb-1">Instant Retrieval</h3>
                                        <p className="text-slate-600 text-sm">No more manual filing or frantic searches during an audit.</p>
                                    </div>
                                </div>

                                <div className="flex gap-4 items-start">
                                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center shrink-0">
                                        <span className="text-emerald-600 text-lg">📋</span>
                                    </div>
                                    <div>
                                        <h3 className="font-bold mb-1">Audit Trail Transparency</h3>
                                        <p className="text-slate-600 text-sm">Every communication and compliance step is logged digitally to demonstrate responsiveness to enforcement officers.</p>
                                    </div>
                                </div>

                                <div className="flex gap-4 items-start">
                                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center shrink-0">
                                        <span className="text-emerald-600 text-lg">🛡️</span>
                                    </div>
                                    <div>
                                        <h3 className="font-bold mb-1">Selective Licensing Shield</h3>
                                        <p className="text-slate-600 text-sm">With licensing schemes launching at a "record-breaking pace," we track borough-specific requirements to prevent <strong>Rent Repayment Orders (RROs)</strong> that can cost landlords over <strong>£10,000</strong>.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Feature 3: Social Housing Regulatory Shield */}
            <section className="py-20 px-6 bg-slate-900 text-white">
                <div className="container mx-auto max-w-6xl">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold mb-6">
                            Built for Social Housing Compliance
                        </h2>
                        <p className="text-xl text-slate-400 max-w-3xl mx-auto">
                            From <strong className="text-white">Awaab's Law</strong> hazard management to <strong className="text-white">Tenant Satisfaction Measures</strong> reporting to institutional-grade financial transparency — everything a registered provider needs in one platform.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="bg-slate-800/50 backdrop-blur-sm p-8 rounded-2xl border border-slate-700 hover:border-blue-500/50 transition-colors group">
                            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg shadow-blue-500/30">
                                <span className="text-white text-2xl">🛡️</span>
                            </div>
                            <h3 className="text-xl font-bold mb-4">Awaab's Law: Full Hazard Compliance</h3>
                            <p className="text-slate-400 mb-4">Every prescribed hazard — damp, mould, excess cold and heat, fire, electrical, structural, pests — scored, timed, and evidenced against the <strong className="text-white">statutory deadlines</strong>.</p>
                            <p className="text-blue-400 text-sm font-medium">Live and ready ahead of the November 2026 Phase 2 expansion.</p>
                        </div>

                        <div className="bg-slate-800/50 backdrop-blur-sm p-8 rounded-2xl border border-slate-700 hover:border-emerald-500/50 transition-colors group">
                            <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg shadow-emerald-500/30">
                                <span className="text-white text-2xl">📊</span>
                            </div>
                            <h3 className="text-xl font-bold mb-4">TSM Reporting Suite</h3>
                            <p className="text-slate-400 mb-4">Build your annual <strong className="text-white">Tenant Satisfaction Measures</strong> submission directly from case and repair data — not a spreadsheet reconstructed from memory each spring.</p>
                            <p className="text-emerald-400 text-sm font-medium">Aligned to the Regulator of Social Housing's published TSM specification.</p>
                        </div>

                        <div className="bg-slate-800/50 backdrop-blur-sm p-8 rounded-2xl border border-slate-700 hover:border-purple-500/50 transition-colors group">
                            <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg shadow-purple-500/30">
                                <span className="text-white text-2xl">📈</span>
                            </div>
                            <h3 className="text-xl font-bold mb-4">Asset Performance Reporting</h3>
                            <p className="text-slate-400 mb-4">Generate professional <strong className="text-white">profit-and-loss reports</strong> and tax forecasts.</p>
                            <p className="text-purple-400 text-sm font-medium">Ensure your portfolio remains profitable and tax-ready.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Feature 4: Cavalier Risk Prevention Engine */}
            <section className="py-20 px-6 bg-gradient-to-b from-white to-slate-50">
                <div className="container mx-auto max-w-6xl">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold mb-6">
                            The "Cavalier" Risk Prevention Engine
                        </h2>
                        <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                            Recent history shows that "cavalier" attitudes toward compliance lead to <strong className="text-slate-900">record-breaking fines</strong>, such as the £579,000 penalty recently upheld against a landlord pair.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        <div className="bg-white p-8 rounded-2xl shadow-lg border border-slate-100 hover:shadow-xl transition-shadow group">
                            <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center mb-6 group-hover:rotate-6 transition-transform shadow-lg shadow-amber-200">
                                <span className="text-white text-3xl">🔔</span>
                            </div>
                            <h3 className="text-xl font-bold mb-4">Safety Check Automation</h3>
                            <p className="text-slate-600">Built-in reminders for <strong>EPCs, EICRs, and Gas Safety</strong> ensure that no statutory obligation "slips through the cracks".</p>
                        </div>

                        <div className="bg-white p-8 rounded-2xl shadow-lg border border-slate-100 hover:shadow-xl transition-shadow group">
                            <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center mb-6 group-hover:rotate-6 transition-transform shadow-lg shadow-red-200">
                                <span className="text-white text-3xl">🏗️</span>
                            </div>
                            <h3 className="text-xl font-bold mb-4">HMO & Planning Guard</h3>
                            <p className="text-slate-600">Prevent draconian fines—like the <strong>£900,000 penalty</strong> for illegal house conversions—by monitoring planning and HMO compliance in real-time.</p>
                        </div>

                        <div className="bg-white p-8 rounded-2xl shadow-lg border border-slate-100 hover:shadow-xl transition-shadow group md:col-span-2 lg:col-span-1">
                            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center mb-6 group-hover:rotate-6 transition-transform shadow-lg shadow-emerald-200">
                                <span className="text-white text-3xl">📊</span>
                            </div>
                            <h3 className="text-xl font-bold mb-4">The Compliance Health Score</h3>
                            <p className="text-slate-600">A dynamic metric that gives you a <strong>"bird's-eye view"</strong> of your total legal exposure, allowing you to rectify issues before they become criminal offences.</p>
                        </div>
                    </div>

                    {/* Stats Banner */}
                    <div className="mt-16 bg-gradient-to-r from-red-600 to-red-700 rounded-2xl p-8 text-white text-center">
                        <p className="text-lg mb-2 text-red-100">Don't become a statistic.</p>
                        <p className="text-3xl md:text-4xl font-bold">
                            £579,000 <span className="text-red-200 text-xl">+ £900,000</span>
                        </p>
                        <p className="text-sm text-red-200 mt-2">Recent fines for "cavalier" landlords who ignored compliance</p>
                    </div>
                </div>
            </section>

            {/* Feature 5: Tenant Activation Gate */}
            <section className="py-20 px-6 bg-slate-50">
                <div className="container mx-auto max-w-6xl">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">

                        <div>
                            <h2 className="text-3xl md:text-4xl font-bold mb-6">
                                The Tenant Activation Gate
                            </h2>
                            <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                                A tenancy on Landlord Pro <strong className="text-slate-900">cannot start, no key can be issued, and the rent ledger stays dormant</strong> until the tenant has clicked their unique acknowledgement link and confirmed every compliance document.
                            </p>

                            <div className="bg-amber-50 border-l-4 border-amber-500 p-5 rounded-r-lg mb-6">
                                <p className="text-sm text-amber-900 font-semibold mb-2">⚠️ First Point of Call: The Tenancy Agreement</p>
                                <p className="text-sm text-amber-800">
                                    Before you can add ANY tenant, you must have an applicable Tenancy Agreement on file. Every tenant is attached to one — the Setup Guide cannot complete without it. This is the first thing you do on the system.
                                </p>
                            </div>

                            <div className="space-y-4">
                                <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
                                    <div className="flex gap-3">
                                        <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center shrink-0">
                                            <span className="text-emerald-600 text-lg">📋</span>
                                        </div>
                                        <div>
                                            <h3 className="font-bold mb-1">Tenant Setup Guide</h3>
                                            <p className="text-slate-600 text-sm">Pick the applicable Tenancy Agreement → enter tenant details → assign to a property → system auto-bundles the compliance pack (Gas Safety, EPC, EICR, How to Rent, Deposit Cert) → emails the tenant a unique acknowledgement link.</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
                                    <div className="flex gap-3">
                                        <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center shrink-0">
                                            <span className="text-red-600 text-lg">🚫</span>
                                        </div>
                                        <div>
                                            <h3 className="font-bold mb-1">Until Tenant Acknowledges — System Blocks</h3>
                                            <p className="text-slate-600 text-sm">No tenant activation, no key handover, no tenancy start, no rent ledger, no deposit-protection seal, no portal access. The system enforces this — you couldn't bypass it if you wanted to.</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
                                    <div className="flex gap-3">
                                        <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center shrink-0">
                                            <span className="text-emerald-600 text-lg">✅</span>
                                        </div>
                                        <div>
                                            <h3 className="font-bold mb-1">Acknowledgement → Gate Releases</h3>
                                            <p className="text-slate-600 text-sm">Tenant clicks the email link, ticks every document, hits Confirm. IP, timestamp, device fingerprint, and cryptographic hash of each document are captured. Tenant flips to Active. Key handover unlocks. Tenancy goes live.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-amber-600 to-orange-700 rounded-2xl p-8 text-white relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-40 h-40 bg-yellow-400/20 rounded-full blur-3xl"></div>
                            <div className="relative z-10">
                                <div className="text-6xl mb-6">🔒</div>
                                <h3 className="text-2xl font-bold mb-4">No Tenancy Without Proof</h3>
                                <p className="text-amber-100 mb-6">
                                    Six months later, when a tenant claims <em>"I never received the Gas Safety Certificate"</em>, you don't argue. You show the timestamp, the IP, the device, and the cryptographic fingerprint of the exact document they confirmed.
                                </p>
                                <div className="border-t border-amber-400/30 pt-6">
                                    <p className="text-yellow-200 font-semibold">
                                        A landlord using Landlord Pro literally cannot start a tenancy without a court-ready audit trail.
                                    </p>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* v2.5 New Capabilities */}
            <section className="py-20 px-6 bg-white border-t border-slate-200">
                <div className="container mx-auto max-w-6xl">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                            <span className="text-lg">✨</span> New in v2.5
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold mb-6">
                            What's New for the Renters' Rights Act 2025
                        </h2>
                        <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                            Five capabilities purpose-built for the new legal regime — and the Private Rented Sector Database register opening October 2025.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">

                        {/* AI Legal Q&A */}
                        <div className="bg-gradient-to-br from-emerald-50 to-white p-8 rounded-2xl border-2 border-emerald-200 hover:shadow-xl transition-shadow">
                            <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-xl flex items-center justify-center mb-6 shadow-lg shadow-emerald-200">
                                <span className="text-white text-2xl">🎓</span>
                            </div>
                            <h3 className="text-xl font-bold mb-3">AI Legal Q&amp;A Assistant</h3>
                            <p className="text-slate-600 mb-4">
                                Ask plain-English questions about possession grounds, deposit rules, HMO law. Get answers with <strong>citations down to the exact section</strong> of the Renters' Rights Act 2025 or Housing Act 1988.
                            </p>
                            <p className="text-emerald-700 text-sm font-semibold">
                                Saves £200–£300 per solicitor question. Refuses high-risk questions and routes to a human.
                            </p>
                        </div>

                        {/* PRS Database Readiness */}
                        <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-2xl border-2 border-blue-200 hover:shadow-xl transition-shadow">
                            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center mb-6 shadow-lg shadow-blue-200">
                                <span className="text-white text-2xl">🏛️</span>
                            </div>
                            <h3 className="text-xl font-bold mb-3">PRS Database Readiness</h3>
                            <p className="text-slate-600 mb-4">
                                Portfolio-level dashboard with clickable tiles (Ready / Partial / Action Required), per-property drill-down, CSV export ready for gov.uk upload, and a guided submission Setup Guide.
                            </p>
                            <p className="text-blue-700 text-sm font-semibold">
                                Purpose-built for the October 2025 register go-live.
                            </p>
                        </div>

                        {/* Notice Date Calculator + Cert of Service */}
                        <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-2xl border-2 border-amber-200 hover:shadow-xl transition-shadow">
                            <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center mb-6 shadow-lg shadow-amber-200">
                                <span className="text-white text-2xl">📅</span>
                            </div>
                            <h3 className="text-xl font-bold mb-3">Notice Date Calculator &amp; Certificate of Service</h3>
                            <p className="text-slate-600 mb-4">
                                Holiday-aware date calculation for every Section 8 ground (1, 1A, 8, 11, 14...). Notice Cert of Service captures method, timestamp, and a cryptographic fingerprint of the served document.
                            </p>
                            <p className="text-amber-700 text-sm font-semibold">
                                Wrong dates = case dismissed. We close that gap.
                            </p>
                        </div>

                        {/* Court-Grade Evidence Pack */}
                        <div className="bg-gradient-to-br from-purple-50 to-white p-8 rounded-2xl border-2 border-purple-200 hover:shadow-xl transition-shadow">
                            <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl flex items-center justify-center mb-6 shadow-lg shadow-purple-200">
                                <span className="text-white text-2xl">📦</span>
                            </div>
                            <h3 className="text-xl font-bold mb-3">Court-Grade Evidence Pack Generator</h3>
                            <p className="text-slate-600 mb-4">
                                One-click bundle: tabbed PDF, page-numbered, cryptographic fingerprints for non-tampering proof, acknowledgement timeline. Formatted to <strong>CPR 55A and Practice Direction 27</strong>.
                            </p>
                            <p className="text-purple-700 text-sm font-semibold">
                                45 seconds to generate. Judges love a clean bundle — possession often granted without a full hearing.
                            </p>
                        </div>

                        {/* Elastic Serverless Infrastructure */}
                        <div className="bg-gradient-to-br from-slate-50 to-white p-8 rounded-2xl border-2 border-slate-200 hover:shadow-xl transition-shadow">
                            <div className="w-14 h-14 bg-gradient-to-br from-slate-700 to-slate-900 rounded-xl flex items-center justify-center mb-6 shadow-lg shadow-slate-300">
                                <span className="text-white text-2xl">☁️</span>
                            </div>
                            <h3 className="text-xl font-bold mb-3">Elastic AWS-Serverless Infrastructure</h3>
                            <p className="text-slate-600 mb-4">
                                Lambda + API Gateway + DynamoDB single-table + S3 + Cognito + SSM Parameter Store. Multi-AZ resilience, 99.99% AWS SLA, no provisioning, no capacity planning.
                            </p>
                            <p className="text-slate-700 text-sm font-semibold">
                                Same code from 1 property to 50,000+. No "Enterprise tier" infrastructure call.
                            </p>
                        </div>

                        {/* Async Bulk Import */}
                        <div className="bg-gradient-to-br from-teal-50 to-white p-8 rounded-2xl border-2 border-teal-200 hover:shadow-xl transition-shadow">
                            <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-teal-700 rounded-xl flex items-center justify-center mb-6 shadow-lg shadow-teal-200">
                                <span className="text-white text-2xl">⚙️</span>
                            </div>
                            <h3 className="text-xl font-bold mb-3">Async Bulk Import</h3>
                            <p className="text-slate-600 mb-4">
                                DynamoDB-backed jobs table + worker Lambda. Upload a portfolio CSV of thousands of properties; get an email when it's done — no more 29-second API Gateway timeouts.
                            </p>
                            <p className="text-teal-700 text-sm font-semibold">
                                Onboard 5,000 properties overnight in a single job.
                            </p>
                        </div>

                    </div>
                </div>
            </section>

            {/* v2.6 New Capabilities — Awaab's Law & Housing Ombudsman */}
            <section className="py-20 px-6 bg-slate-50 border-t border-slate-200">
                <div className="container mx-auto max-w-6xl">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                            <span className="text-lg">🆕</span> New in v2.6
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold mb-6">
                            What's New for Awaab's Law &amp; the Housing Ombudsman
                        </h2>
                        <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                            Six capabilities built for the November 2026 Awaab's Law Phase 2 expansion — and the Housing Ombudsman's 2024 Complaint Handling Code.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">

                        {/* HHSRS Statutory Hazard Engine */}
                        <div className="bg-gradient-to-br from-emerald-50 to-white p-8 rounded-2xl border-2 border-emerald-200 hover:shadow-xl transition-shadow">
                            <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-xl flex items-center justify-center mb-6 shadow-lg shadow-emerald-200">
                                <span className="text-white text-2xl">🌡️</span>
                            </div>
                            <h3 className="text-xl font-bold mb-3">HHSRS Statutory Hazard Engine</h3>
                            <p className="text-slate-600 mb-4">
                                Score every Category 1/2 hazard against the full HHSRS matrix, with a tamper-evident cryptographic seal the moment an officer confirms it.
                            </p>
                            <p className="text-emerald-700 text-sm font-semibold">
                                Built to the same standard local authority EHOs use — so nobody can argue your score.
                            </p>
                        </div>

                        {/* Awaab's Law Phase 2 — all six hazards */}
                        <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-2xl border-2 border-blue-200 hover:shadow-xl transition-shadow">
                            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center mb-6 shadow-lg shadow-blue-200">
                                <span className="text-white text-2xl">🛡️</span>
                            </div>
                            <h3 className="text-xl font-bold mb-3">Awaab's Law Phase 2 — All Six Hazards</h3>
                            <p className="text-slate-600 mb-4">
                                Excess Cold, Excess Heat, Fire, Electrical, Structural Collapse and Pest Infestation now all score, classify, and trigger emergency accommodation offers automatically.
                            </p>
                            <p className="text-blue-700 text-sm font-semibold">
                                Live and ready months ahead of the 30 November 2026 deadline.
                            </p>
                        </div>

                        {/* Ombudsman Determination Tracker */}
                        <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-2xl border-2 border-amber-200 hover:shadow-xl transition-shadow">
                            <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center mb-6 shadow-lg shadow-amber-200">
                                <span className="text-white text-2xl">⚖️</span>
                            </div>
                            <h3 className="text-xl font-bold mb-3">Housing Ombudsman Determination Tracker</h3>
                            <p className="text-slate-600 mb-4">
                                Log a determination once, and the platform tracks every order and recommendation to its deadline, chases the evidence, and builds the follow-up submission for you.
                            </p>
                            <p className="text-amber-700 text-sm font-semibold">
                                No more spreadsheets tracking Ombudsman compliance by hand.
                            </p>
                        </div>

                        {/* Compensation Calculator & Policy Engine */}
                        <div className="bg-gradient-to-br from-purple-50 to-white p-8 rounded-2xl border-2 border-purple-200 hover:shadow-xl transition-shadow">
                            <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl flex items-center justify-center mb-6 shadow-lg shadow-purple-200">
                                <span className="text-white text-2xl">💷</span>
                            </div>
                            <h3 className="text-xl font-bold mb-3">Compensation Calculator &amp; Policy Engine</h3>
                            <p className="text-slate-600 mb-4">
                                Calculate Ombudsman remedies and civil damages side by side, and route every offer through manager and legal sign-off before it goes out.
                            </p>
                            <p className="text-purple-700 text-sm font-semibold">
                                Every approved offer is archived with a cryptographic seal — provably untampered.
                            </p>
                        </div>

                        {/* TSM Publication Summary Generator */}
                        <div className="bg-gradient-to-br from-teal-50 to-white p-8 rounded-2xl border-2 border-teal-200 hover:shadow-xl transition-shadow">
                            <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-teal-700 rounded-xl flex items-center justify-center mb-6 shadow-lg shadow-teal-200">
                                <span className="text-white text-2xl">📊</span>
                            </div>
                            <h3 className="text-xl font-bold mb-3">TSM Publication Summary Generator</h3>
                            <p className="text-slate-600 mb-4">
                                One-click generation of the 14 mandatory items the Regulator of Social Housing requires in your annual Tenant Satisfaction Measures publication.
                            </p>
                            <p className="text-teal-700 text-sm font-semibold">
                                Built directly from the RSH's published TSM specification, not a guess.
                            </p>
                        </div>

                        {/* Fire Safety & Building Control Interlock */}
                        <div className="bg-gradient-to-br from-red-50 to-white p-8 rounded-2xl border-2 border-red-200 hover:shadow-xl transition-shadow">
                            <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-orange-600 rounded-xl flex items-center justify-center mb-6 shadow-lg shadow-red-200">
                                <span className="text-white text-2xl">🔥</span>
                            </div>
                            <h3 className="text-xl font-bold mb-3">Fire Safety &amp; Building Control Interlock</h3>
                            <p className="text-slate-600 mb-4">
                                Automatically flags cladding-related fire risk work that needs Building Control sign-off, and drafts resident and fire service notifications the moment a fire hazard is confirmed.
                            </p>
                            <p className="text-red-700 text-sm font-semibold">
                                Fire safety and repairs stop running in separate silos.
                            </p>
                        </div>

                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="py-20 bg-gradient-to-br from-emerald-900 via-emerald-800 to-slate-900 text-white px-6 text-center relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySDI0di0yaC0xMnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-50"></div>
                <div className="relative z-10 container mx-auto max-w-4xl">
                    <div className="inline-block bg-emerald-600/30 backdrop-blur-sm border border-emerald-400/30 rounded-full px-4 py-2 mb-6">
                        <span className="text-emerald-300 text-sm font-medium">🛡️ The Legal Shield for Every Portfolio</span>
                    </div>
                    <h2 className="text-3xl md:text-5xl font-bold mb-6">
                        See it on your portfolio in 15 minutes.
                    </h2>
                    <p className="text-emerald-100 max-w-2xl mx-auto mb-10 text-lg">
                        Whether you manage 20 rentals or 50,000 units — book a live walkthrough and we'll show you exactly how the legal shield works against your real compliance exposure.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <a
                            href="https://calendar.app.google/E2NdqWZSh2csxTAC7"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block px-10 py-5 bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold rounded-lg transition-colors shadow-xl text-lg"
                        >
                            Book a 15-minute platform demo →
                        </a>
                        <Link to="/" className="inline-block px-10 py-5 bg-transparent border-2 border-white/30 text-white font-bold rounded-lg hover:bg-white/10 transition-colors text-lg">
                            View Pricing
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Features;
